import { UIMessage } from '@ai-sdk/react';
import { Box, Stack } from '@mui/material';
import { ToolUIPart, UIMessagePart, isTextUIPart, isDataUIPart, isToolUIPart, DynamicToolUIPart } from 'ai';
import { all, isEmpty, isNotEmpty } from 'ramda';
import { ReactNode, memo } from 'react';

import { UIChatMessage } from '../chat.types';
import { TOOLS } from '../tools';

import { AssistantAvatar } from './AssistantAvatar';
import { ChatThinkingMessage } from './ChatThinkingMessage';
import { ChatMessageTextPart } from './parts/ChatMessageTextPart';
import { ChatMessageThinkingPart } from './parts/ChatMessageThinkingPart';
import { ChatMessageToolPart } from './parts/ChatMessageToolPart';
import { MessageSuggestionsPart } from './parts/MessageSuggestionsPart';

// Placeholder type for eventual data parts
// currently no data parts are used and thus `never` is appropriate
type DATA_PARTS = never;

export type ChatAssistantMessageProps = {
  message: UIChatMessage;
  isStreaming?: boolean;
  isLastMessage: boolean;
};

const hasToolOutput = (part: ToolUIPart<any> | DynamicToolUIPart): boolean => {
  return part.state === 'output-available' || part.state === 'output-error';
};

interface PartComponentProps {
  part: UIMessagePart<DATA_PARTS, TOOLS>;
  isLastPart?: boolean;
  isStreaming?: boolean;
}
const PartComponent = (props: PartComponentProps) => {
  const { part, isLastPart, isStreaming } = props;
  const isLastPartStreaming = isLastPart && isStreaming;

  // Render tool parts (structured input/output) with their own components
  // with fallback to `GenericTool` if the actual tool is unknown
  // Also includes unspecified status between tool output and streaming of
  // next step while still streaming (indicating processing is not done yet)
  if (isToolUIPart(part)) {
    return (
      <>
        <ChatMessageToolPart part={part} />
        {hasToolOutput(part) && isLastPartStreaming && <ChatMessageThinkingPart />}
      </>
    );
  }

  // Render text parts, ensuring that the last text part being streamed is handled correctly

  if (isTextUIPart(part)) {
    return <ChatMessageTextPart part={part} isStreaming={isLastPartStreaming} />;
  }

  return null;
};

const ChatAssistantMessageInternal = ({
  message,
  isLastMessage,
  isStreaming,
}: ChatAssistantMessageProps): ReactNode => {
  // Casting to more specific message allowing tool parts rendering
  const typedMessage = message as UIMessage<unknown, DATA_PARTS, TOOLS>;

  // Prevents rendering of *ANY* data output (this likely includes workflows as well)
  // This needs a proper revisiting once we want to render workflows directly in the chat
  // and specifically avoid rendering `data-om-*` parts
  const isDataPartMessage = isNotEmpty(typedMessage.parts) && all(isDataUIPart, typedMessage.parts);
  if (isDataPartMessage) {
    return null;
  }

  return (
    <Box
      sx={{
        alignSelf: 'flex-start',
        minHeight: isLastMessage ? 96 : 'auto',
        maxWidth: '100%',
        counterReset: 'citation',
      }}
    >
      {isEmpty(typedMessage.parts) ? (
        <ChatThinkingMessage />
      ) : (
        <Stack direction={'row'} spacing={2}>
          <AssistantAvatar />
          <Stack gap={3} minWidth="50%">
            {typedMessage.parts.map((part, index, parts) => {
              const key = `${message.id}-part-${index}`;
              const isLastPart = index === parts.length - 1;

              return (
                <PartComponent
                  key={key}
                  part={part}
                  isLastPart={isLastPart}
                  isStreaming={isLastMessage && isStreaming}
                />
              );
            })}
            {
              // only show suggestions for the last assistant message
            }
            <MessageSuggestionsPart isStreaming={isStreaming} isLastMessage={isLastMessage} message={message} />
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

// memoize each assistant message for performance/re-rendering
export const ChatAssistantMessage = memo(ChatAssistantMessageInternal);
